import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

const formatNumber = (num) =>
  new Intl.NumberFormat('de-DE').format(Math.floor(num || 0));

const StatisticsView = ({ data, mode = 'view' }) => {
  const {
    title = '',
    stats = [],
    columns = 'four',
    color = 'blue',
    align = 'left',
    showAnimation = true,
  } = data;

  const sectionRef = useRef(null);
  const [active, setActive] = useState(false);

  const isFullWidth = !!data.setFullWidth;
  const widthClass = isFullWidth ? 'fcmh-statistics--full-width' : '';

  useEffect(() => {
    if (mode === 'edit') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 },
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);
    return () => observer.disconnect();
  }, [mode]);

  const bg =
    color === 'gray'
      ? '#f4f4f4'
      : color === 'white'
        ? '#ffffff'
        : color === 'yellow'
          ? '#e8a817'
          : color === 'purple'
            ? '#82378c'
            : '#00859b'; // default blue

  const fg =
    color === 'blue' || color === 'purple'
      ? '#ffffff'
      : color === 'yellow'
        ? '#0a0a0a'
        : '#00859b';

  return (
    <section
      ref={sectionRef}
      className={cx(
        'fcmh-statistics',
        `fcmh-statistics--${color} ${widthClass}`,
      )}
      style={{ backgroundColor: bg, color: fg }}
    >
      <div className="fcmh-statistics__inner">
        {title && (
          <h2 className="fcmh-statistics__title" style={{ textAlign: align }}>
            {title}
          </h2>
        )}

        <div
          className={cx(
            'fcmh-statistics__grid',
            `fcmh-statistics__grid--${columns}`,
          )}
        >
          {stats.map((item, i) => (
            <Stat
              key={i}
              item={item}
              align={align}
              animate={active && showAnimation}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Stat = ({ item, align, animate }) => {
  const valueRef = useRef(null);

  useEffect(() => {
    if (!animate || !valueRef.current) {
      if (valueRef.current)
        valueRef.current.textContent = formatNumber(item.value);
      return;
    }

    const target = parseInt(item.value) || 0;
    const duration = 1000;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const val = target * progress;
      valueRef.current.textContent = formatNumber(val);
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [animate, item.value]);

  return (
    <div className="fcmh-statistics__item" style={{ textAlign: align }}>
      <div ref={valueRef} className="fcmh-statistics__value">
        {formatNumber(0)}
      </div>
      <div className="fcmh-statistics__label">{item.label}</div>
      {item.info && <div className="fcmh-statistics__info">{item.info}</div>}
    </div>
  );
};

export default StatisticsView;
